import React from "react";
import { generatePath, Link, useParams } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import { Trans, useTranslation } from "react-i18next";
import homeBanner from "src/assets/home-banner.webp";
import { Autoplay, Navigation } from "swiper/modules";
import Routes from "src/constants/routes";
import { usePagination } from "src/hooks";

import { BusinessCard, BusinessCardSkeleton, BusinessListItem, BusinessListItemSkeleton } from "../../components";
import { useCategoryBusinesses } from "../../hooks";
import {
  BusinessesList,
  Carousel,
  CarouselSlide,
  CarouselWrapper,
  CategoryWrapper,
  HeroBanner,
  HeroImage,
  HeroOverlay,
  HeroTitle,
  SearchBox,
  SearchIcon,
  SearchInput,
  Title,
} from "./CategoryBusinesses.styles";

const CategoryPage: React.FC = () => {
  const city = "New York";

  const { t } = useTranslation();
  const { id: categoryIdString } = useParams();
  const categoryId = parseInt(categoryIdString || "0", 10);

  const { current, pageSize, onPageChange } = usePagination();
  const { loading, businesses, totalCount } = useCategoryBusinesses({ id: categoryId, page: current, pageSize });

  return (
    <>
      <HeroBanner>
        <HeroImage src={homeBanner} alt="Category Banner" />
        <HeroOverlay>
          <HeroTitle>
            <Trans i18nKey="common.bannerTitle" components={{ 1: <br /> }} />
          </HeroTitle>
          <SearchBox>
            <SearchIcon>
              <SearchOutlined />
            </SearchIcon>
            <SearchInput type="text" placeholder={t("common.searchPlaceholder")} />
          </SearchBox>
        </HeroOverlay>
      </HeroBanner>
      <CategoryWrapper>
        <Title>
          <Trans i18nKey="category.recommendations" />
        </Title>

        <CarouselWrapper>
          <Carousel
            loop
            slidesPerView="auto"
            direction="horizontal"
            spaceBetween={16}
            pagination={{
              clickable: true,
            }}
            navigation
            modules={[Autoplay, Navigation]}
          >
            {businesses.map((business) => (
              <CarouselSlide key={business.id}>
                <Link to={generatePath(Routes.business, { id: business.id })}>
                  <BusinessCard business={business} />
                </Link>
              </CarouselSlide>
            ))}

            {loading &&
              Array(4)
                .fill(0)
                .map((_, index) => (
                  <CarouselSlide key={`business-card-skeleton-${index}`}>
                    <BusinessCardSkeleton />
                  </CarouselSlide>
                ))}
          </Carousel>
        </CarouselWrapper>

        <Title>
          We found {businesses.length} barbers in {city}
        </Title>

        {!loading && (
          <BusinessesList
            itemLayout="vertical"
            size="large"
            dataSource={businesses}
            renderItem={(business, index) => <BusinessListItem key={index} business={business} />}
            pagination={{
              position: "bottom",
              align: "center",
              total: totalCount,
              current,
              pageSize,
              onChange: onPageChange,
            }}
          />
        )}

        {loading &&
          Array(4)
            .fill(0)
            .map((_, index) => <BusinessListItemSkeleton key={`business-list-item-skeleton-${index}`} />)}
      </CategoryWrapper>
    </>
  );
};

export default CategoryPage;
